import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ArrowRight, BookOpen, ClipboardCheck } from 'lucide-react'
import { getCorrectiveActions } from '../../lib/services/correctiveActions'
import { listRecommendationDrafts } from '../../lib/services/training'

interface Finding {
  finding_id?: string
  domain?: string
  severity?: number
  observation?: string
  standard_ref?: string
}

interface Props {
  visitId: string
  findings: Finding[]
}

export function ImprovementPlanSection({ visitId, findings }: Props) {
  const actionsQuery = useQuery({
    queryKey: ['improvement-plan-actions', visitId],
    queryFn: () => getCorrectiveActions({ visitId }),
  })

  const recommendationsQuery = useQuery({
    queryKey: ['improvement-plan-recommendations'],
    queryFn: () => listRecommendationDrafts(),
  })

  const actions = actionsQuery.data ?? []
  const recommendations = (recommendationsQuery.data ?? []).filter(
    (r) => r.status === 'approved',
  )

  // Match recommendations to findings by standard_ref
  const findingStdRefs = new Set(findings.map((f) => f.standard_ref).filter(Boolean))
  const linkedRecommendations = recommendations.filter(
    (r) => r.standard_id && findingStdRefs.has(r.standard_id),
  )

  if (actions.length === 0 && linkedRecommendations.length === 0) return null

  return (
    <div className="space-y-4">
      <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">
        Improvement Plan
      </h3>

      {actions.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-[var(--brand-deep)]">
            <ClipboardCheck size={14} />
            Corrective Actions ({actions.length})
          </div>
          <div className="space-y-2">
            {actions.map((action) => (
              <Link
                key={action.action_id}
                to={`/actions/${action.action_id}`}
                className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 transition-colors hover:border-[var(--brand-primary)] hover:bg-slate-50"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">
                    {action.title}
                  </p>
                  <p className="text-xs text-slate-500">
                    {action.owner_role && <span className="mr-2">Owner: {action.owner_role}</span>}
                    {action.due_date && <span>Due: {new Date(action.due_date).toLocaleDateString()}</span>}
                  </p>
                </div>
                <div className="ml-3 flex items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                      action.status === 'completed' || action.status === 'verified'
                        ? 'bg-emerald-100 text-emerald-700'
                        : action.status === 'overdue'
                          ? 'bg-rose-100 text-rose-700'
                          : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {action.status}
                  </span>
                  <ArrowRight size={14} className="text-slate-400" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {linkedRecommendations.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-[var(--brand-deep)]">
            <BookOpen size={14} />
            Training Recommendations ({linkedRecommendations.length})
          </div>
          <div className="space-y-2">
            {linkedRecommendations.map((rec) => (
              <div
                key={rec.recommendation_id}
                className="rounded-lg border border-slate-200 px-3 py-2"
              >
                <p className="text-sm font-semibold text-slate-800">
                  {rec.education_modules?.title ?? 'Training Module'}
                </p>
                <p className="text-xs text-slate-500">
                  Standard: {rec.standard_id}
                </p>
                <p className="mt-1 text-xs text-slate-600">{rec.recommendation_text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
